import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import ProjectManagementPage from './pages/ProjectManagementPage';
import LinkedDataPage from './pages/LinkedDataPage';
import ProcessFlowPage from './pages/ProcessFlowPage';
import PfmeaTablePage from './pages/PfmeaTablePage';
import PfmeaEditorPage from './pages/PfmeaEditorPage';
import WorkflowDashboardPage from './pages/WorkflowDashboardPage';
import ActionTrackerPage from './pages/ActionTrackerPage';
import IdeationPage from './pages/IdeationPage';
import AiAgentsPage from './pages/AiAgentsPage';
import ControlPlanPage from './pages/ControlPlanPage';
import WorkInstructionsPage from './pages/WorkInstructionsPage';
import SettingsPage from './pages/SettingsPage';
import AIAssistantPage from './pages/AIAssistantPage';
import FunctionMatrixPage from './pages/FunctionMatrixPage';
import ProjectScopingPage from './pages/ProjectScopingPage';
import FailureNetPage from './pages/FailureNetPage';
import FailureChainsPage from './pages/FailureChainsPage';
import ReportsPage from './pages/ReportsPage';
import UserGuidePage from './pages/UserGuidePage';
import ComplianceAuditorPage from './pages/ComplianceAuditorPage';
import { LearnProvider } from './contexts/LearnContext';

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
};

const App: React.FC = () => {
    const { user, loading, logout } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-500 mx-auto mb-4"></div>
                    <p className="text-[var(--text-secondary)]">Loading Magnus PFMEA...</p>
                </div>
            </div>
        );
    }

    // =========================================================================
    // ISO 27001 CRITICAL SECURITY CHECK
    // Block unapproved users BEFORE showing any app content
    // =========================================================================
    const isApproved = user?.isApproved === true && user?.role !== 'pending';
    const isAutoGainAdmin = user?.role === 'autoGainAdmin';

    if (user && !isApproved && !isAutoGainAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
                <div className="text-center max-w-md p-8 bg-[var(--bg-secondary)] rounded-xl shadow-2xl border border-[var(--border-color)]">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">Access Pending</h2>
                    <p className="text-[var(--text-secondary)] mb-6">
                        Your account is awaiting administrator approval. You will be notified once your access has been granted.
                    </p>
                    <p className="text-sm text-[var(--text-muted)] mb-6">
                        Email: {user.email}
                    </p>
                    <button
                        onClick={() => logout()}
                        className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                    >
                        Sign Out
                    </button>
                </div>
            </div>
        );
    }

    return (
        <LearnProvider>
            <Routes>
                <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" replace />} />

                <Route path="/" element={
                    <ProtectedRoute>
                        <Layout />
                    </ProtectedRoute>
                }>
                    <Route index element={<Dashboard />} />
                    <Route path="ai-assistant" element={<AIAssistantPage />} />
                    <Route path="projects" element={<ProjectManagementPage />} />
                    <Route path="linked-data" element={<LinkedDataPage />} />
                    <Route path="project-scoping" element={<ProjectScopingPage />} />
                    <Route path="process-flow" element={<ProcessFlowPage />} />
                    <Route path="function-matrix" element={<FunctionMatrixPage />} />
                    <Route path="pfmea" element={<PfmeaTablePage />} />
                    <Route path="pfmea-editor" element={<PfmeaEditorPage />} />
                    <Route path="failure-chains" element={<FailureChainsPage />} />
                    <Route path="workflow" element={<WorkflowDashboardPage />} />
                    <Route path="actions" element={<ActionTrackerPage />} />
                    <Route path="ideation" element={<IdeationPage />} />
                    <Route path="ai-agents" element={<AiAgentsPage />} />
                    <Route path="control-plan" element={<ControlPlanPage />} />
                    <Route path="work-instructions" element={<WorkInstructionsPage />} />
                    <Route path="failure-net" element={<FailureNetPage />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="compliance-auditor" element={<ComplianceAuditorPage />} />
                    <Route path="user-guide" element={<UserGuidePage />} />
                    <Route path="settings" element={<SettingsPage />} />
                </Route>

                {/* Catch all - redirect to dashboard */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </LearnProvider>
    );
};

export default App;
